/**
 * Urban Traveller & Co - Luxury Minimalist Header
 * File: assets/utc-header.js
 */

(function() {
  'use strict';

  class UTCHeader {
    constructor() {
      // Elements
      this.header = document.querySelector('.utc-header');
      this.menuToggle = document.querySelector('[data-menu-toggle]');
      this.menuClose = document.querySelector('[data-menu-close]');
      this.mobileMenu = document.querySelector('[data-mobile-menu]');
      this.overlayBackdrop = document.querySelector('[data-overlay-backdrop]');
      this.searchToggle = document.querySelector('[data-search-toggle]');
      this.searchBar = document.querySelector('[data-search-bar]');
      this.searchClose = document.querySelector('[data-search-close]');
      this.searchInput = document.querySelector('[data-search-input]');
      this.cartIcon = document.querySelector('[data-cart-icon]');
      this.cartBadge = document.querySelector('[data-cart-count]');

      // State
      this.scrollThreshold = 50;

      this.init();
    }

    init() {
      if (!this.header) return;

      this.bindEvents();
      this.initStickyHeader();
      this.initCartUpdates();

      // Make sure page content starts below the fixed header
      this.updateLayoutOffset();
      window.addEventListener('resize', () => this.updateLayoutOffset());
    }

    bindEvents() {
      // Mobile menu
      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', () => this.openMobileMenu());
      }
      if (this.menuClose) {
        this.menuClose.addEventListener('click', () => this.closeMobileMenu());
      }
      if (this.overlayBackdrop) {
        this.overlayBackdrop.addEventListener('click', () => this.closeMobileMenu());
      }

      // Mobile submenu toggles
      this.initMobileSubmenus();
      this.initMobileNestedSubmenus();

      // Search bar
      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', () => this.toggleSearch());
      }
      if (this.searchClose) {
        this.searchClose.addEventListener('click', () => this.closeSearch());
      }

      // Close search / menu on ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeMobileMenu();
          this.closeSearch();
        }
      });

      // Prevent body scroll when menu is open (touch devices)
      document.addEventListener(
        'touchmove',
        (e) => {
          if (document.body.classList.contains('menu-open') && this.mobileMenu) {
            if (!this.mobileMenu.contains(e.target)) {
              e.preventDefault();
            }
          }
        },
        { passive: false }
      );
    }

    // ============================================
    // Mobile Submenus (first level accordion)
    // ============================================
    initMobileSubmenus() {
      if (!this.mobileMenu) return;

      const toggles = this.mobileMenu.querySelectorAll('[data-mobile-submenu-toggle]');
      if (!toggles.length) return;

      toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          const submenu = toggle.nextElementSibling;
          
          if (!submenu || !submenu.classList.contains('utc-overlay-nav__submenu')) return;

          // Toggle state
          toggle.setAttribute('aria-expanded', !isExpanded);
          
          if (isExpanded) {
            submenu.classList.remove('is-open');
          } else {
            submenu.classList.add('is-open');
          }
        });
      });
    }

    // ============================================
    // Mobile Nested Submenus (second level accordion)
    // ============================================
    initMobileNestedSubmenus() {
      if (!this.mobileMenu) return;

      const nestedToggles = this.mobileMenu.querySelectorAll('[data-mobile-nested-toggle]');
      if (!nestedToggles.length) return;

      nestedToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          const submenu = toggle.nextElementSibling;
          
          if (!submenu || !submenu.classList.contains('utc-overlay-nav__nested-submenu')) return;

          // Toggle state
          toggle.setAttribute('aria-expanded', !isExpanded);
          
          if (isExpanded) {
            submenu.classList.remove('is-open');
          } else {
            submenu.classList.add('is-open');
          }
        });
      });
    }

    // ============================================
    // Mobile Menu Functions
    // ============================================
    openMobileMenu() {
      if (!this.mobileMenu) return;

      this.mobileMenu.classList.add('active');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      if (this.overlayBackdrop) {
        this.overlayBackdrop.classList.add('active');
      }
      document.body.classList.add('menu-open');

      const firstLink = this.mobileMenu.querySelector('a');
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 300);
      }
    }

    closeMobileMenu() {
      if (!this.mobileMenu) return;

      this.mobileMenu.classList.remove('active');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      if (this.overlayBackdrop) {
        this.overlayBackdrop.classList.remove('active');
      }
      document.body.classList.remove('menu-open');

      // Close all open submenus when closing main menu
      const openSubmenus = this.mobileMenu.querySelectorAll('.utc-overlay-nav__submenu.is-open, .utc-overlay-nav__nested-submenu.is-open');
      openSubmenus.forEach(submenu => {
        submenu.classList.remove('is-open');
      });

      // Reset all aria-expanded states
      const allToggles = this.mobileMenu.querySelectorAll('[data-mobile-submenu-toggle], [data-mobile-nested-toggle]');
      allToggles.forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false');
      });

      if (this.menuToggle) {
        this.menuToggle.focus();
      }
    }

    // ============================================
    // Search Functions
    // ============================================
    toggleSearch() {
      if (!this.searchBar) return;

      const isActive = this.searchBar.classList.contains('active');
      if (isActive) {
        this.closeSearch();
      } else {
        this.openSearch();
      }
    }

    openSearch() {
      if (!this.searchBar) return;

      this.searchBar.classList.add('active');

      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.focus();
        }
      }, 300);
    }

    closeSearch() {
      if (!this.searchBar) return;

      this.searchBar.classList.remove('active');

      if (this.searchInput) {
        this.searchInput.value = '';
      }
      if (this.searchToggle) {
        this.searchToggle.focus();
      }
    }

    // ============================================
    // Sticky Header (simple)
    // ============================================
    initStickyHeader() {
      const onScroll = () => this.handleScroll();
      window.addEventListener('scroll', onScroll);
      this.handleScroll();
    }

    handleScroll() {
      if (!this.header) return;

      const currentScrollY = window.scrollY || window.pageYOffset || 0;

      if (currentScrollY > this.scrollThreshold) {
        this.header.classList.add('sticky');
      } else {
        this.header.classList.remove('sticky');
      }
    }

    // ============================================
    // Layout: avoid clipping content under header
    // ============================================
    updateLayoutOffset() {
      if (!this.header) return;

      const main = document.querySelector('#MainContent');
      if (!main) return;

      const isOverlap = this.header.getAttribute('overlap') === 'true';

      if (isOverlap) {
        // Transparent / overlap mode: let hero sit behind header
        main.style.paddingTop = '0px';
      } else {
        // Normal pages: push content down by header height
        const height = this.header.offsetHeight || 0;
        main.style.paddingTop = `${height}px`;
      }
    }

    // ============================================
    // Cart Badge Updates
    // ============================================
    initCartUpdates() {
      document.addEventListener('cart:updated', (event) => {
        this.updateCartBadge(event.detail?.item_count);
      });

      this.interceptCartRequests();
      this.refresh();
    }

    updateCartBadge(itemCount) {
      if (!this.cartBadge) return;

      const count = parseInt(itemCount, 10) || 0;
      this.cartBadge.textContent = count;

      this.cartBadge.classList.add('pulse');
      setTimeout(() => {
        this.cartBadge.classList.remove('pulse');
      }, 400);

      if (count === 0) {
        this.cartBadge.style.display = 'none';
      } else {
        this.cartBadge.style.display = 'flex';
      }
    }

    interceptCartRequests() {
      const originalFetch = window.fetch;
      const self = this;

      // ---- fetch ----
      window.fetch = async function(...args) {
        const response = await originalFetch.apply(this, args);

        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input.url;

          if (typeof url === 'string' && url.includes('/cart')) {
            const clonedResponse = response.clone();
            const data = await clonedResponse.json();

            if (data && typeof data.item_count !== 'undefined') {
              self.updateCartBadge(data.item_count);
            } else {
              self.refresh();
            }
          }
        } catch (e) {
          self.refresh();
        }

        return response;
      };

      // ---- XMLHttpRequest ----
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function(method, url) {
        this._url = url;
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function() {
        this.addEventListener('load', function() {
          try {
            if (this._url && typeof this._url === 'string' && this._url.includes('/cart')) {
              const selfXHR = self;
              try {
                const data = JSON.parse(this.responseText);
                if (data && typeof data.item_count !== 'undefined') {
                  selfXHR.updateCartBadge(data.item_count);
                } else {
                  selfXHR.refresh();
                }
              } catch (e) {
                selfXHR.refresh();
              }
            }
          } catch (e) {
            // ignore
          }
        });

        return originalSend.apply(this, arguments);
      };
    }

    // ============================================
    // Public API
    // ============================================
    refresh() {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((data) => {
          if (data && typeof data.item_count !== 'undefined') {
            this.updateCartBadge(data.item_count);
          }
        })
        .catch((error) => {
          console.error('Error fetching cart:', error);
        });
    }
  }

  function initUTCHeader() {
    window.utcHeader = new UTCHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUTCHeader);
  } else {
    initUTCHeader();
  }

  // Global helper if you ever need to manually bump the badge
  window.updateCartBadge = function(count) {
    if (window.utcHeader) {
      window.utcHeader.updateCartBadge(count);
    }
  };
})();